import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import DomainOutlinedIcon from "@mui/icons-material/DomainOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { usePlanoContext } from "../../contexts/PlanoContext.tsx";
import { usePermissoes } from "../../contexts/PermissoesContext.tsx";
import { parseJwt } from "../../utils/token.util.ts";
import { getPlanLimits } from "../../utils/planLimits.util.ts";

const DOMINIO_ADMIN = "@controlsis.com.br";

interface NavLeaf {
  text: string;
  icon: React.ReactNode;
  path: string;
  hidden?: boolean;
}

interface NavGroup {
  label: string;
  items: NavLeaf[];
}

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onNavigate?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapsed, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isPlanActive, planoNome, isLoading } = usePlanoContext();
  const { isAdm, tipo, podeVerLog, isLoading: carregandoPermissoes } = usePermissoes();

  const emailUsuario = parseJwt(localStorage.getItem("token"))?.sub as string | undefined;
  const isAdmin = !!emailUsuario && emailUsuario.toLowerCase().endsWith(DOMINIO_ADMIN);
  const isEnterprise = getPlanLimits(planoNome).isEnterprise;

  const groups: NavGroup[] = [
    {
      label: "Operação",
      items: [
        { text: "Cirurgias", icon: <MonitorHeartOutlinedIcon fontSize="small" />, path: "/cirurgias" },
        { text: "Agenda", icon: <CalendarMonthOutlinedIcon fontSize="small" />, path: "/agenda" },
        {
          text: "Dashboard",
          icon: <InsightsOutlinedIcon fontSize="small" />,
          path: "/dashboard",
          hidden: !isEnterprise || tipo === "INSTRUMENTADOR",
        },
      ],
    },
    {
      label: "Cadastros",
      items: [
        // Cadastros ficam visiveis para todos: o instrumentador precisa
        // consultar medico/paciente/hospital, so nao pode editar. Esconder a
        // tela inteira tiraria dele uma consulta legitima.
        { text: "Médicos", icon: <MedicalServicesOutlinedIcon fontSize="small" />, path: "/medico/listagem" },
        { text: "Pacientes", icon: <PersonOutlineIcon fontSize="small" />, path: "/paciente/listagem" },
        { text: "Hospitais", icon: <DomainOutlinedIcon fontSize="small" />, path: "/hospital/listagem" },
        {
          text: "Instrumentadores",
          icon: <BadgeOutlinedIcon fontSize="small" />,
          path: "/instrumentador/listagem",
        },
        { text: "Convênios", icon: <HealthAndSafetyOutlinedIcon fontSize="small" />, path: "/convenio/listagem" },
        {
          text: "Tipo de cirurgia",
          icon: <AddCircleOutlineIcon fontSize="small" />,
          path: "/tipo-cirurgia/listagem",
        },
      ],
    },
    {
      // Grupo novo, preparado para receber outros relatórios. O Dashboard
      // continua em "Operação": é Enterprise-only e tem gate próprio, misturar
      // os dois confundiria coisas diferentes.
      label: "Relatórios",
      items: [
        {
          text: "Log de atividades",
          icon: <FactCheckOutlinedIcon fontSize="small" />,
          path: "/relatorios/log-atividades",
          hidden: !podeVerLog,
        },
      ],
    },
    {
      label: "Conta",
      items: [
        // Assinatura, planos e usuarios sao coisa de dono da conta. Configuracoes
        // fica para todos: e la que cada um edita o proprio perfil e troca a
        // propria senha.
        {
          text: "Minha assinatura",
          icon: <WorkspacePremiumOutlinedIcon fontSize="small" />,
          path: "/meu-plano",
          hidden: !isAdm,
        },
        {
          text: "Ver planos",
          icon: <AddCircleOutlineIcon fontSize="small" />,
          path: "/planos/listagem",
          hidden: !isAdm,
        },
        // "Usuários" saiu daqui: a gestão virou a aba Usuários de Configurações,
        // para não existirem duas portas para a mesma coisa.
        { text: "Configurações", icon: <SettingsOutlinedIcon fontSize="small" />, path: "/configuracoes" },
      ],
    },
    {
      label: "ControlSIS",
      items: [
        {
          text: "Painel Administrativo",
          icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
          path: "/admin",
          hidden: !isAdmin,
        },
      ],
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onNavigate && window.innerWidth <= 960) onNavigate();
  };

  return (
    <aside className="ds-sidebar">
      <div className="ds-sidebar-brand">
        <button
          type="button"
          className="ds-hamburger-btn"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
        >
          <ChevronLeftIcon fontSize="small" style={{ transform: collapsed ? "rotate(180deg)" : "none" }} />
        </button>
        <div className="ds-brand-mark">
          <img src="/logo-controlsis.png" alt="ControlSIS" />
        </div>
        <div className="ds-brand-text">
          <strong>ControlSIS</strong>
        </div>
      </div>

      <nav className="ds-sidebar-nav">
        {!isLoading && !carregandoPermissoes &&
          groups.map((group) => {
            const visibleItems = group.items.filter((item) => !item.hidden);
            if (visibleItems.length === 0) return null;
            if (group.label !== "Conta" && !isPlanActive) return null;
            return (
              <div className="ds-nav-group" key={group.label}>
                <div className="ds-nav-group-label">{group.label}</div>
                {visibleItems.map((item) => (
                  <button
                    key={item.text}
                    type="button"
                    className={`ds-nav-item ${location.pathname === item.path ? "active" : ""}`}
                    onClick={() => handleNavigation(item.path)}
                  >
                    {item.icon}
                    <span className="ds-nav-item-label">{item.text}</span>
                  </button>
                ))}
              </div>
            );
          })}
      </nav>

      <div className="ds-sidebar-footer">
        <div className="ds-avatar">{(emailUsuario ?? "?").slice(0, 2).toUpperCase()}</div>
        <div className="who">
          <strong>{emailUsuario ?? "Usuário"}</strong>
          <span>{isAdmin ? "Equipe ControlSIS" : planoNome || "Sem plano"}</span>
        </div>
      </div>

      <button
        type="button"
        className="ds-sidebar-collapse-fab"
        onClick={onToggleCollapsed}
        aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
      >
        <ChevronLeftIcon fontSize="small" />
      </button>
    </aside>
  );
};

export default Sidebar;
